/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import CheckBoxView from '@airasia-phoenix/react-native/components/CheckBoxView';
import TextView, {
  type TextViewProps,
} from '@airasia-phoenix/react-native/components/TextView';
import { getTextStyle, pd } from '@airasia-phoenix/react-native/utils/utils';
import type { TFunction } from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import {
  usePrivacyStatementUrl,
  useTermsOfUseUrl,
} from '../../hooks/useAccountUrl';
import { useOpenUrl } from '../../hooks/useOpenUrl';
import styles from './styles';

export interface ChinaTermsAgreementViewProps {
  isSelected: boolean;
  onToggle: () => void;
}

export const buildChinaTermsAgreementLabel = (
  t: TFunction,
  onTermsOfUsePress: () => void,
  onPrivacyPolicyPress: () => void,
): TextViewProps => {
  const typescale = pd.sys.typescale.caption_text;
  const linkStyle = getTextStyle(typescale, pd.sys.colour.interactive_link);

  return {
    text: t('signUpView.chinaTermsAgreement'),
    style: getTextStyle(typescale, pd.sys.colour.content_secondary),
    spannableTextsProps: [
      {
        spannable: t('signUpView.termsOfUse'),
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: linkStyle,
        onPress: onTermsOfUsePress,
      },
      {
        spannable: t('signUpView.privacyPolicy'),
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: linkStyle,
        onPress: onPrivacyPolicyPress,
      },
    ],
  };
};

export function ChinaTermsAgreementView({
  isSelected,
  onToggle,
}: ChinaTermsAgreementViewProps) {
  const { t } = useTranslation('account');
  const openUrl = useOpenUrl();
  const termsOfUseUrl = useTermsOfUseUrl();
  const privacyStatementUrl = usePrivacyStatementUrl();

  const label = useMemo(
    () =>
      buildChinaTermsAgreementLabel(
        t,
        () => openUrl(termsOfUseUrl),
        () => openUrl(privacyStatementUrl),
      ),
    [openUrl, privacyStatementUrl, t, termsOfUseUrl],
  );

  return (
    <View style={styles.chinaTermsRow} testID="china-terms-row">
      <View style={styles.chinaTermsCheckbox} collapsable={false}>
        <CheckBoxView
          isSelected={isSelected}
          onPress={onToggle}
          testID="china-terms-checkbox"
        />
      </View>
      <View style={styles.chinaTermsLabel}>
        <TextView {...label} testID="china-terms-label" />
      </View>
    </View>
  );
}
